import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

export default function RewardPointsTab({ loading, rewardPoints }) {
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-48">
                <ArrowPathIcon className="h-7 w-7 text-gray-950 animate-spin" />
                <p className="text-xs text-gray-500 mt-2.5 font-bold uppercase tracking-wider">Fetching Reward Points...</p>
            </div>
        );
    }

    if (!rewardPoints) {
        return (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <p className="text-sm font-semibold uppercase tracking-wider">No Reward Points Data Available</p>
            </div>
        );
    }

    const formatCurrency = (val, symbol = "₹") => {
        if (val === undefined || val === null || val === "N/A") return "N/A";
        const num = Number(val);
        if (isNaN(num)) return val;
        return `${symbol}${num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr === "N/A") return "N/A";
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric"
            });
        } catch (e) {
            return dateStr;
        }
    };

    const d = rewardPoints;
    const decodeHtmlEntity = (str) => {
        if (!str) return "";
        return str.replace(/&#8377;/g, "₹").replace(/&amp;/g, "&");
    };

    const currentPoints = d.current_points ?? 0;
    const monetaryValue = d.monetary_value ?? 0;
    const conversionRate = d.points_conversion_rate ?? 0;
    const currencySymbol = decodeHtmlEntity(d.currency) || "₹";

    const referral = d.referral || {};
    const refCode = referral.code || "N/A";
    const refEarned = referral.total_earned ?? 0;
    const refCount = referral.total_referrals ?? 0;

    const historyList = d.history || [];

    return (
        <div className="space-y-6 w-full text-xs">
            {/* Section 1: Summary Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 border border-black bg-black text-white rounded-lg shadow-sm flex flex-col justify-between">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Current Points</p>
                    <p className="text-3xl font-black mt-2 tracking-tight">{currentPoints}</p>
                </div>

                <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-xs flex flex-col justify-between">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Monetary Value</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(monetaryValue, currencySymbol)}</p>
                </div>

                <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-xs flex flex-col justify-between">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Conversion Rate</p>
                    <p className="text-lg font-semibold text-gray-805 mt-2">
                        {conversionRate > 0 ? `1 Point = ${formatCurrency(conversionRate, currencySymbol)}` : "—"}
                    </p>
                </div>

                <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-xs flex flex-col justify-between">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Referral Earnings</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-2">{formatCurrency(refEarned, currencySymbol)}</p>
                </div>
            </div>

            {/* Section 2: Split Information Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-xs space-y-3">
                    <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b pb-1.5">
                        Member Information
                    </h4>
                    <div className="space-y-2">
                        <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-400 font-medium">User Name</span>
                            <span className="font-semibold text-gray-800">{d.user_name || "N/A"}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-400 font-medium">User Email</span>
                            <span className="font-semibold text-gray-800 break-all">{d.user_email || "N/A"}</span>
                        </div>
                        <div className="flex justify-between items-center py-1">
                            <span className="text-gray-400 font-medium">User ID</span>
                            <span className="font-mono text-gray-800 font-bold">{d.user_id || "N/A"}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-xs space-y-3">
                    <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b pb-1.5">
                        Referral Information
                    </h4>
                    <div className="space-y-2">
                        <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-400 font-medium">Referral Code</span>
                            <span className="font-mono font-bold text-gray-800 bg-gray-50 px-2 py-0.5 rounded border border-gray-150">{refCode}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-400 font-medium">Total Referrals</span>
                            <span className="font-semibold text-gray-800">{refCount}</span>
                        </div>
                        <div className="flex justify-between items-center py-1">
                            <span className="text-gray-400 font-medium">Total Referral Earned</span>
                            <span className="font-semibold text-emerald-700">{formatCurrency(refEarned, currencySymbol)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 3: Points Summary Details */}
            <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-xs space-y-3">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b pb-1.5">
                    Points Summary
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                        <p className="text-[10px] text-gray-400 font-medium">Current Points</p>
                        <p className="text-sm font-bold text-gray-850 mt-1">{currentPoints}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 font-medium">Monetary Value</p>
                        <p className="text-sm font-bold text-gray-850 mt-1">{formatCurrency(monetaryValue, currencySymbol)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 font-medium">Currency</p>
                        <p className="text-sm font-bold text-gray-850 mt-1 uppercase">{currencySymbol}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 font-medium">Conversion Rate</p>
                        <p className="text-sm font-bold text-gray-855 mt-1">{conversionRate}</p>
                    </div>
                </div>
            </div>

            {/* Section 4: Points History Table */}
            <div className="space-y-3">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b pb-1.5">
                    Points History
                </h4>
                {historyList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-24 border border-dashed border-gray-200 rounded-lg bg-gray-50/50 text-gray-400 font-medium">
                        No Reward Point Transactions Found
                    </div>
                ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 text-left text-[11px]">
                            <thead className="bg-gray-50 text-gray-700 font-bold uppercase tracking-wider">
                                <tr>
                                    <th className="px-3 py-2.5">Date</th>
                                    <th className="px-3 py-2.5 text-center">Points</th>
                                    <th className="px-3 py-2.5">Type</th>
                                    <th className="px-3 py-2.5">Description</th>
                                    <th className="px-3 py-2.5">Reference</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-150 text-gray-600 bg-white">
                                {historyList.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-3 py-2.5 font-medium whitespace-nowrap">{formatDate(item.date)}</td>
                                        <td className={`px-3 py-2.5 text-center font-bold ${Number(item.points) >= 0 ? "text-green-600" : "text-red-600"}`}>
                                            {Number(item.points) >= 0 ? `+${item.points}` : item.points}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${String(item.type).toLowerCase() === "credit" || String(item.type).toLowerCase() === "earned"
                                                ? "bg-green-50 text-green-700"
                                                : "bg-red-50 text-red-700"
                                                }`}>
                                                {item.type || "—"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-normal max-w-xs">{item.description || "—"}</td>
                                        <td className="px-3 py-2.5 font-mono text-gray-500">{item.reference || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
